/** @jsxImportSource @emotion/react */
import {
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
} from "@chakra-ui/react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import dayjs from "dayjs";
import * as HTMLEntities from "html-entities";
import { cloneDeep, debounce } from "lodash";
import { useRouter } from "next/router";
import React, { useCallback, useEffect, useState } from "react";
import { BsPlayFill, BsThreeDots } from "react-icons/bs";
import { FaRegTrashAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import ReactTooltip from "react-tooltip";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import sanitize from "sanitize-html";
import Notes from "../../../../public/Notes";
import * as CampaignApi from "../../../api/Campaign";
import { launchCampaign } from "../../../api/Campaign";
import { CampaignActionAtom } from "../../../atoms/CampaignActionAtom";
import {
  ActiveCampaignAtom,
  TempCampaignAtom,
} from "../../../atoms/CampaignAtom";
import { CampaignUnsavedChangesAtom } from "../../../atoms/CampaignUnsavedChangesAtom";
import {
  CampaignScreen,
  CampaingBuilderFormAtom,
} from "../../../atoms/CampaingBuilderFormAtom";
import { LabelAtom } from "../../../atoms/LabelAtom";
import { NodeIndexAtom } from "../../../atoms/NodeIndexAtom";
import { UserAtom } from "../../../atoms/UserAtom";
import { ICampaignStatus } from "../../../generated/Api";
import useClickOutside from "../../../hook/useClickOutside";
import { Flex, notSelect } from "../../../styles/general";
import { Campaign, INodeTypes } from "../../../types/CampaignTypes";
import {
  HelpVideoNameMap,
  IHelpContexts,
} from "../../../types/HelpVideosTypes";
import { ILabel } from "../../../types/LabelTypes";
import { useUnsavedChangesBlocker } from "../../../utils/UnsavedChangesBlocker";
import { HelpVideo } from "../../HelpVideo";
import IconBerserkerMail from "../../Icon";
import LabelSelect from "../../Label/LabelSelects";
import Warning from "../../Modal/container/Warning";
import { useModal } from "../../Modal/Modal";
import StyledButton from "../../StyledButton";
import StyledSwitch from "../../Switch";
import WhatsNewButton from "../../WhatsNewButton";
import NoteModal from "../Table/NoteModal";
import CampaignSettings from "../../Modal/container/CampaignSettings";
import CampaignSettingsIcon from "../../../assets/icons/campaign-settings";
import { sanitizeHtmlOptions } from "../../../utils/SanitizeBerserkerEmail";

const PopoverHeaderCSS = css`
  font-size: 14px;
  font-weight: 400;
  line-height: 26px;
  text-transform: uppercase;
  color: #707070;

  padding: 8px 24px 0;
`;

const Icon = styled.img`
  width: 10px;
  height: 10px;
  margin-left: 8px;
  ${notSelect}
`;

const PopoverButton = styled.div`
  width: 200px;

  padding: 6px 12px;

  border-radius: 4px;

  display: flex;
  align-items: center;
  justify-content: start;
  grid-gap: 8px;

  transition: background 0.3s ease-in-out;

  cursor: pointer;

  svg {
    width: 14px;

    color: #bbbbbb;

    transition: color 0.3s ease-in-out;
  }

  .button-label {
    font-size: 14px;
    line-height: 26px;
    font-weight: 400;
    color: #333333;

    transition: color 0.3s ease-in-out;
  }

  &:hover {
    background: #fb972e;

    .button-label {
      color: white;
    }

    svg {
      color: white;
    }
  }
`;

const Wrapper = styled.div`
  width: calc(100% - 60px);
  background: #ffffff;
  border-bottom: 1px solid #eaeaea;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px;
  position: fixed;
  z-index: 99;

  .icon-button {
    min-width: 40px;
    max-width: 40px;
    min-height: 40px;
    max-height: 40px;

    display: flex;
    align-items: center;
    justify-content: center;

    background: #fff;

    border-radius: 5px;
    border: 1px solid #dddddd;

    cursor: pointer;

    svg {
      width: 20px;
      height: 20px;

      color: #555555;
    }

    .tooltip-text {
      font-size: 14px;
      font-weight: 600;
      letter-spacing: 0px;
      color: #ffffff;
    }
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  grid-gap: 16px;
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  cursor: default;
`;

const TopBarTitle = styled.div`
  font-size: 20px;
  letter-spacing: 0.3px;
  font-weight: 600;
  color: #333333;
  display: flex;
  align-items: center;
`;

const TopBarSubTitle = styled.div`
  font-size: 14px;
  letter-spacing: 0.2px;
  color: #707070;
`;

const Button = styled(StyledButton)`
  margin: 0px 10px;
`;

const MoreOptionPopoverContainer = styled.div`
  width: 160px;
  height: fit-content;

  background: white;

  position: absolute;
  top: 44px;
  right: 0;
  z-index: 20;

  border-radius: 5px;
  box-shadow: 0px 3px 6px #00000026;

  border: 1px solid #d4d4d4;
  border-bottom: none;
  border-top: none;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  div,
  button {
    width: 100%;
  }
`;

const bottomButtonsCss = css`
  width: 5em;
  padding: 0 10px;
  position: relative;
  color: #555555;
  text-align: center;
  align-items: center;
  display: flex;
  justify-content: center;
  height: 50px;
  cursor: pointer;
  user-select: none;
  font-weight: 600;
  font-size: 15px;

  transition: all 0.3s ease-in-out;

  &:hover {
    background: #efefef;
  }
`;

const bottomActionsCss = css`
  width: 110px;
  .merge-fields-container {
    top: -220px;
  }
`;

export function getBody(
  editorHTML: string,
  fontFamily: string,
  fontSize: number,
  maxWidth: number,
  isEmpty: boolean
) {
  while (editorHTML.includes("></p>")) {
    editorHTML = editorHTML.replace("></p>", ">&nbsp;</p>");
  }

  if (!isEmpty) {
    let text = sanitize(editorHTML);
    text = HTMLEntities.decode(text);
    if (text.trim().length === 0) isEmpty = true;
  }

  const newBody = sanitize(editorHTML, sanitizeHtmlOptions);

  let body = isEmpty
    ? ""
    : `<pre style="overflow-wrap: break-word; white-space: pre-wrap; 
          line-height: 1.5em;
          letter-spacing: 0.025em;
          font-family: ${fontFamily || "Arial"}; font-size:${
        fontSize || 14
      }px; width: 100%; max-width: ${
        maxWidth ? `${maxWidth}px` : "100%"
      }">${newBody}</pre>`;

  return body;
}

interface Props {
  validateCampaign: () => Promise<number[]>;
  saveCampaign: (campaignId: any, updatedCampaign: any) => any;
}

const TopBar = ({ validateCampaign, saveCampaign }: Props): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const open = () => setIsOpen(!isOpen);
  const close = () => setIsOpen(false);

  const [activeCampaign, setActiveCampaign] =
    useRecoilState(ActiveCampaignAtom);
  const [tempCampaign, setTempCampaign] = useRecoilState<Campaign | null>(
    TempCampaignAtom
  );

  const [openDropView, setOpenDropView] = useState<boolean>(false);
  const wrapperRef = useClickOutside(() => setOpenDropView(false));
  const labels = useRecoilValue(LabelAtom);
  const [loading, setLoading] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const user = useRecoilValue(UserAtom);
  const [action, setCampaignAction] = useRecoilState(CampaignActionAtom);
  const setCampaingBuilderForm = useSetRecoilState(CampaingBuilderFormAtom);
  const setNodeIndex = useSetRecoilState(NodeIndexAtom);
  const modal = useModal();
  const router = useRouter();

  const { campaignUnsavedChangesBlocker } = useUnsavedChangesBlocker();
  const [unsavedChanges, setUnsavedChanges] = useRecoilState(
    CampaignUnsavedChangesAtom
  );

  const browserTabcloseHandler = (e) => {
    e.preventDefault(); // If you prevent default behavior in Mozilla Firefox prompt will always be shown
    // Chrome requires returnValue to be set
    e.returnValue = "";
  };

  useEffect(() => {
    if (window) {
      router.beforePopState(() => {
        if (unsavedChanges) {
          const result = window.confirm(
            "There are unsaved changes. Are you sure you want to leave?"
          );
          return result;
        } else {
          return true;
        }
      });
      window.onbeforeunload = browserTabcloseHandler;
    }

    return () => {
      if (window) {
        window.onbeforeunload = null;
      }
      router.beforePopState(() => {
        return true;
      });
    };
  }, [router, unsavedChanges]);

  useEffect(() => {
    if (!activeCampaign) router.push("/new-campaign");
  }, [activeCampaign]);

  async function onInitialClick(label: ILabel) {
    if (!activeCampaign || !tempCampaign) return;
    setCampaingBuilderForm(CampaignScreen.TAG);
    setNodeIndex(-1);
    selectLabelHandler(label);
  }

  const debouncedSaveCampaign = useCallback(
    debounce(async (campaignId, updatedCampaign) => {
      try {
        // setLoadingSave(true);
        await saveCampaign(campaignId, updatedCampaign);
      } catch (error) {
        console.log(error);
      } finally {
        // setLoadingSave(false);
      }
    }, 300),
    [user, saveCampaign]
  );

  useEffect(() => {
    if (!tempCampaign?._id || action !== "edit") return;
    // @ts-ignore
    debouncedSaveCampaign(tempCampaign._id, tempCampaign);
  }, [debouncedSaveCampaign, tempCampaign, action]);

  async function handleLaunch() {
    if (!tempCampaign || loading) return;
    const erroredNodes = await validateCampaign();
    if (erroredNodes.length) return;
    setLoading(true);
    try {
      const response = await launchCampaign(tempCampaign._id);
      if (response) {
        setActiveCampaign(response);

        setTempCampaign(response);
        setCampaignAction("create");
        setCampaingBuilderForm(null);
        setUnsavedChanges(false);
        toast("Success", { type: "success" });
      }
    } catch (err) {
      toast(err.message, { type: "error" });
      console.log(err);
    } finally {
      setLoading(false);
    }
  }

  async function handleEditCampaign() {
    if (!tempCampaign || loading) return;
    const erroredNodes = await validateCampaign();
    if (erroredNodes.length) return;
    setLoading(true);

    const fontFamily = user?.emailOptions.font.family;
    const fontSize = user?.emailOptions.font.size;
    const maxWidth = user?.emailOptions.maxWidth;

    let copyTempCampaign = cloneDeep(tempCampaign);

    copyTempCampaign.nodes = copyTempCampaign.nodes.map((node) => {
      if (node.type === INodeTypes.Email && node.email?.interestingURLs) {
        let interestingURLs = node.email.interestingURLs;
        let sanitizedInterestTags: { [key: string]: string[] } = {};
        if (interestingURLs) {
          Object.keys(interestingURLs).forEach((tag) => {
            sanitizedInterestTags[tag.split(".").join("|")] =
              interestingURLs[tag];
          });
        }
        node.email.interestingURLs = sanitizedInterestTags;
        node.email.body = getBody(
          node.email.body,
          fontFamily,
          fontSize,
          maxWidth,
          false
        );
        return node;
      } else {
        return node;
      }
    });

    try {
      const response = await CampaignApi.editCampaign(
        copyTempCampaign._id,
        copyTempCampaign as any
      );
      if (response) {
        setActiveCampaign(response);
        setTempCampaign(response);
        setCampaignAction("create");
        setCampaingBuilderForm(null);
        toast("Success", { type: "success" });
      }
    } catch (err) {
      toast(err.message, { type: "error" });
      console.log(err);
    } finally {
      setUnsavedChanges(false);
      setLoading(false);
    }
  }

  async function handleSaveCampaign() {
    setLoadingSave(true);
    await saveCampaign(tempCampaign._id, tempCampaign);
    toast("Saved", { type: "success" });
    setLoadingSave(false);
  }

  async function toggleActiveCampaign(status: ICampaignStatus) {
    if (!activeCampaign || loading) return;
    if (status === ICampaignStatus.Active) {
      const erroredNodes = await validateCampaign();
      if (erroredNodes.length) return;
    }

    setLoading(true);

    try {
      const response = await CampaignApi.toggleCampaignStatus(
        activeCampaign._id,
        {
          status,
        }
      );
      if (response) {
        setActiveCampaign(response);
        setTempCampaign(response);
        setCampaignAction(
          status === ICampaignStatus.Inactive ? "edit" : "create"
        );
        setCampaingBuilderForm(null);
      }
    } catch (err) {
      toast(err.message, { type: "error" });
      console.log(err);
    } finally {
      setLoading(false);
    }
  }

  function warnChangeStatus(status: ICampaignStatus) {
    const statusAux =
      status === ICampaignStatus.Archive ? "archiving" : "inactivating";
    const description = `By ${statusAux} your campaign, all current contacts in this
                campaign will be removed from its flow.
                    Are you sure you want to continue?`;
    modal.pushModal(
      <Warning
        icon={"./icons/delete.svg"}
        title={`${
          status === ICampaignStatus.Archive ? "Archieve" : "Inactivate"
        } campaign`}
        description={description}
        buttonsName={["No", "Yes"]}
        onOkClicked={() => {
          toggleActiveCampaign(status);
          modal.popModal();
        }}
        onCancelClicked={() => modal.popModal()}
      />
    );
  }

  async function changeStatus(status: ICampaignStatus) {
    try {
      setLoading(true);
      const response = await CampaignApi.toggleCampaignStatus(
        activeCampaign._id,
        { status }
      );
      if (response) {
        toast("Campaign status updated", { type: "success" });
        setOpenDropView(false);
        setTimeout(() => {
          router.push("/new-campaign");
        }, 500);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  function confirmDeleteCampaign() {
    setLoading(true);
    modal.pushModal(
      <Warning
        icon={"./icons/delete.svg"}
        title="Delete Campaign"
        description={`By deleting your campaign, all current contacts in this
            campaign will be removed from its flow.
                    Are you sure you want to continue?`}
        buttonsName={["No", "Yes"]}
        onOkClicked={async () => {
          try {
            changeStatus(ICampaignStatus.Archive);
          } catch (e) {
            console.log(e);
          } finally {
            modal.popModal();
          }
        }}
        onCancelClicked={() => {
          setLoading(false);
          modal.popModal();
        }}
      />
    );
  }

  const openNotesModal = () => {
    modal.pushModal(
      <NoteModal
        campaignId={activeCampaign._id}
        campaignNotes={activeCampaign.notes}
        campaignLastNotesUpdate={activeCampaign.lastNotesUpdate}
        campaignName={activeCampaign.name}
      />,
      "Notes"
    );
  };

  const openSettingsModal = () => {
    modal.pushModal(
      <CampaignSettings
        campaignId={activeCampaign._id}
        campaignOptions={activeCampaign.campaignOptions}
      />,
      "Campaign Settings"
    );
  };

  async function selectLabelHandler(label: ILabel) {
    if (activeCampaign) {
      const isLabelIn = activeCampaign.labels.find(
        (activeLabel) => activeLabel === label._id
      );

      const newLabelsArray = isLabelIn
        ? activeCampaign.labels.filter((_id) => {
            return _id !== label._id;
          })
        : [...activeCampaign.labels, label._id];

      const updatedCampaign = {
        ...activeCampaign,
        labels: newLabelsArray,
      };

      await CampaignApi.updateCampaign(updatedCampaign._id, updatedCampaign);

      setActiveCampaign(updatedCampaign);
    }
  }

  useEffect(() => {
    global.ps_config = {
      workspace_id: "f8ea5b12-675a-43e1-af7e-19f0464c62d8",
    };

    let script = document.createElement("script");
    script.setAttribute("type", "text/javascript");
    script.setAttribute("src", "https://cdn.loopedin.io/js/widget.js?v=0.8");
    document.getElementsByTagName("head")[0].appendChild(script);
  }, []);

  return (
    <Wrapper>
      <div style={{ display: "flex", alignItems: "flex-start" }}>
        <TitleContainer>
          <Flex>
            <TopBarTitle
              css={css`
                color: #707070;
                cursor: pointer;
              `}
              onClick={() => {
                campaignUnsavedChangesBlocker(() => {
                  saveCampaign(tempCampaign._id, tempCampaign);
                  setActiveCampaign(null);
                });
              }}
            >
              Campaigns
            </TopBarTitle>
            <div style={{ margin: "0px 8px 0px 10px" }}>
              <IconBerserkerMail name="navigation-arrow-right" />
            </div>
            <TopBarTitle>
              {activeCampaign?.name || "Create New Campaign"}
              {activeCampaign?.labels && (
                <LabelSelect
                  selectLabelHandler={onInitialClick}
                  activeLabels={labels!.filter((l) =>
                    activeCampaign.labels.includes(l._id)
                  )}
                />
              )}
            </TopBarTitle>
          </Flex>
          <TopBarSubTitle>
            {activeCampaign?.createdAt &&
              `Created on ${dayjs(activeCampaign?.createdAt).format(
                "MMM D, YYYY [@] h:mm a"
              )}`}
            {activeCampaign?.status !== ICampaignStatus.Draft &&
              `      Launched on ${dayjs(activeCampaign?.launchedAt).format(
                "MMM D, YYYY [@] h:mm a"
              )}`}
          </TopBarSubTitle>
        </TitleContainer>
      </div>

      <ButtonsContainer>
        <div
          className="icon-button"
          onClick={() =>
            modal.pushModal(
              <HelpVideo contextTag={IHelpContexts.Campaign} />,
              HelpVideoNameMap[IHelpContexts.Campaign]
            )
          }
          data-tip
          data-for="openVideo"
        >
          <BsPlayFill />
          <ReactTooltip
            id="openVideo"
            place="bottom"
            effect="solid"
            globalEventOff="click"
            backgroundColor="#333333"
            css={css`
              width: fit-content !important;
              padding: 6px 8px !important;
            `}
          >
            <div className="tooltip-text">Watch Quick Help Video</div>
          </ReactTooltip>
        </div>

        <WhatsNewButton />

        <Popover
          returnFocusOnClose={false}
          isOpen={isOpen}
          onClose={close}
          placement="top-start"
        >
          <PopoverTrigger>
            <div className="icon-button" onClick={open}>
              <BsThreeDots />
            </div>
          </PopoverTrigger>
          <PopoverContent width="fit-content">
            <PopoverHeader css={PopoverHeaderCSS}>Actions</PopoverHeader>
            <PopoverBody>
              <PopoverButton onClick={openSettingsModal}>
                <CampaignSettingsIcon />
                <div className="button-label">Settings</div>
              </PopoverButton>

              <PopoverButton onClick={openNotesModal}>
                <Notes />
                <div className="button-label">Add/Edit Notes</div>
              </PopoverButton>

              <PopoverButton onClick={confirmDeleteCampaign}>
                <FaRegTrashAlt />
                <div className="button-label">Delete Campaign</div>
              </PopoverButton>
            </PopoverBody>
          </PopoverContent>
        </Popover>

        <div
          css={css`
            height: 40px;
            position: relative;
            @media (min-width: 960px) {
              display: none;
            }
          `}
          onClick={() => setOpenDropView(!openDropView)}
        >
          <StyledButton
            css={css`
              height: 40px;
            `}
          >
            More Options
          </StyledButton>

          {openDropView && !activeCampaign?.campaignOptions.fixed && (
            <MoreOptionPopoverContainer ref={wrapperRef}>
              {activeCampaign?.status === ICampaignStatus.Draft && (
                <button
                  onClick={() => {
                    handleLaunch();
                    setUnsavedChanges(false);
                  }}
                  disabled={loading}
                  css={css`
                    ${bottomButtonsCss}
                    ${bottomActionsCss}
                    &:hover {
                      background: #efefef;
                    }
                    background: transparent;
                    border: none;
                    &:disabled {
                      opacity: 0.5;
                      cursor: default;
                    }
                  `}
                >
                  Launch
                </button>
              )}

              {tempCampaign?.status === ICampaignStatus.Draft && (
                <Button
                  disabled={loadingSave}
                  isLoading={loadingSave}
                  onClick={handleSaveCampaign}
                  css={css`
                    ${bottomButtonsCss}
                    ${bottomActionsCss}
                    &:hover {
                      background: #efefef;
                    }
                    background: transparent;
                    border: none;
                    &:disabled {
                      opacity: 0.5;
                      cursor: default;
                    }
                  `}
                >
                  Save Draft
                </Button>
              )}

              {tempCampaign?.status === ICampaignStatus.Inactive && (
                <Button
                  disabled={loadingSave}
                  isLoading={loadingSave}
                  onClick={handleSaveCampaign}
                  css={css`
                    ${bottomButtonsCss}
                    ${bottomActionsCss}
                    &:hover {
                      background: #efefef;
                    }
                    background: transparent;
                    border: none;
                    &:disabled {
                      opacity: 0.5;
                      cursor: default;
                    }
                  `}
                >
                  Save
                </Button>
              )}

              {!activeCampaign?.campaignOptions.fixed && action === "create" && (
                <Button
                  disabled={loading}
                  onClick={handleEditCampaign}
                  isLoading={loading}
                  css={css`
                    ${bottomButtonsCss}
                    ${bottomActionsCss}
                    &:hover {
                      background: #efefef;
                    }
                    background: transparent;
                    border: none;
                    &:disabled {
                      opacity: 0.5;
                      cursor: default;
                    }
                  `}
                >
                  Save
                </Button>
              )}
            </MoreOptionPopoverContainer>
          )}
        </div>

        {!activeCampaign?.campaignOptions.fixed && action === "create" && (
          <Button
            isLoading={loading}
            disabled={loading}
            onClick={handleEditCampaign}
            inverted
            css={css`
              margin: 0;
              height: 40px;
              @media screen and (max-width: 959px) {
                display: none;
              }
            `}
          >
            Save
          </Button>
        )}

        {tempCampaign?.status === ICampaignStatus.Inactive && (
          <StyledButton
            inverted
            isLoading={loadingSave}
            disabled={loadingSave}
            onClick={handleSaveCampaign}
            css={css`
              width: fit-content;
              height: 40px;
              @media screen and (max-width: 959px) {
                display: none;
              }
            `}
          >
            Save
          </StyledButton>
        )}

        {tempCampaign?.status === ICampaignStatus.Draft && (
          <StyledButton
            inverted
            isLoading={loadingSave}
            disabled={loadingSave}
            onClick={handleSaveCampaign}
            css={css`
              width: fit-content;
              height: 40px;

              @media screen and (max-width: 959px) {
                display: none;
              }
            `}
          >
            Save Draft
          </StyledButton>
        )}

        {!activeCampaign?.campaignOptions.fixed &&
          activeCampaign?.status !== ICampaignStatus.Draft && (
            <StyledSwitch
              css={css`
                margin: 0;
                height: 40px;
                @media screen and (max-width: 959px) {
                  display: none;
                }
              `}
              toggled={activeCampaign?.status === ICampaignStatus.Active}
              whenToggled={() =>
                activeCampaign?.status === ICampaignStatus.Active
                  ? warnChangeStatus(ICampaignStatus.Inactive)
                  : toggleActiveCampaign(ICampaignStatus.Active)
              }
            />
          )}

        {activeCampaign?.status === ICampaignStatus.Draft && (
          <Button
            onClick={handleLaunch}
            isLoading={loading}
            disabled={loading}
            css={css`
              margin: 0;
              height: 40px;

              @media screen and (max-width: 959px) {
                display: none;
              }
            `}
          >
            Launch
          </Button>
        )}
      </ButtonsContainer>
    </Wrapper>
  );
};

export default TopBar;
